import React, { useEffect, useState } from 'react';

// ── Brand-gradient audio wave (matches landing page) ──────────────────────────
const TW_BARS = [
  { c:'rgba(0,0,202,0.40)',   h:'10px', d:'0.00s', r:'0.75s' },
  { c:'rgba(0,0,210,0.45)',   h:'18px', d:'0.07s', r:'0.95s' },
  { c:'rgba(0,0,222,0.50)',   h:'28px', d:'0.14s', r:'1.15s' },
  { c:'rgba(0,0,230,0.52)',   h:'16px', d:'0.21s', r:'0.85s' },
  { c:'rgba(0,0,240,0.55)',   h:'36px', d:'0.28s', r:'0.75s' },
  { c:'rgba(0,0,248,0.58)',   h:'24px', d:'0.35s', r:'1.00s' },
  { c:'rgba(0,0,253,0.62)',   h:'40px', d:'0.42s', r:'1.15s' },
  { c:'rgba(0,0,253,0.65)',   h:'30px', d:'0.49s', r:'0.85s' },
  { c:'rgba(5,0,250,0.65)',   h:'46px', d:'0.42s', r:'0.75s' },
  { c:'rgba(18,0,245,0.65)',  h:'36px', d:'0.35s', r:'0.95s' },
  { c:'rgba(35,0,238,0.63)',  h:'42px', d:'0.28s', r:'1.10s' },
  { c:'rgba(65,8,222,0.62)',  h:'50px', d:'0.21s', r:'0.85s' },
  { c:'rgba(95,18,208,0.60)', h:'38px', d:'0.14s', r:'0.75s' },
  { c:'rgba(135,28,185,0.58)',h:'54px', d:'0.07s', r:'1.00s' },
  { c:'rgba(168,44,155,0.58)',h:'44px', d:'0.00s', r:'0.80s' },
  { c:'rgba(200,66,118,0.58)',h:'38px', d:'0.07s', r:'1.10s' },
  { c:'rgba(222,88,70,0.60)', h:'50px', d:'0.14s', r:'0.90s' },
  { c:'rgba(240,115,25,0.62)',h:'36px', d:'0.21s', r:'0.75s' },
  { c:'rgba(252,150,4,0.65)', h:'44px', d:'0.28s', r:'1.05s' },
  { c:'rgba(255,168,2,0.70)', h:'28px', d:'0.35s', r:'0.85s' },
  { c:'rgba(255,168,4,0.72)', h:'40px', d:'0.42s', r:'0.78s' },
  { c:'rgba(255,168,4,0.70)', h:'22px', d:'0.49s', r:'0.96s' },
  { c:'rgba(246,160,5,0.65)', h:'34px', d:'0.42s', r:'1.14s' },
  { c:'rgba(234,134,12,0.62)',h:'18px', d:'0.35s', r:'0.84s' },
  { c:'rgba(222,108,18,0.60)',h:'28px', d:'0.28s', r:'0.75s' },
  { c:'rgba(212,78,10,0.57)', h:'14px', d:'0.21s', r:'0.94s' },
  { c:'rgba(204,51,0,0.52)',  h:'22px', d:'0.14s', r:'1.14s' },
  { c:'rgba(200,48,6,0.45)',  h:'10px', d:'0.07s', r:'0.84s' },
];

function ThinkingWave({ active }) {
  return (
    <div className={`tv-brand-wave${active ? ' tv-brand-wave-active' : ''}`} aria-hidden="true">
      {TW_BARS.map((bar, i) => (
        <span
          key={i}
          style={{ '--tw-c': bar.c, '--tw-h': bar.h, '--tw-d': bar.d, '--tw-r': bar.r }}
        />
      ))}
    </div>
  );
}

const FUN_FACTS = [
  'Episodes with an open-loop hook in the first 8 seconds retain 30% more viewers.',
  'YouTube titles under 60 characters get 18% higher CTR on average.',
  'The "curiosity gap" technique appears in 9 of the top 10 podcast hooks analyzed.',
  'Episodes that promise a specific outcome convert listeners to subscribers 2× faster.',
  'The best re-engagement moment happens at the 40% mark — right when attention dips.',
  'Emotionally specific language increases share rates by up to 35%.',
  'Podcasts that launch with a guest grow 3× faster than solo launches.',
  'The average listener decides in the first 60 seconds whether to keep going.',
];

// Checkmark SVG
function CheckIcon() {
  return (
    <svg width="9" height="8" viewBox="0 0 9 8" fill="none">
      <path d="M1 4l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function ThinkingView({ topic, steps, currentIndex, done }) {
  const [elapsed, setElapsed]       = useState(0);
  const [factIdx, setFactIdx]       = useState(0);
  const [factVisible, setFactVisible] = useState(true);

  // Elapsed clock
  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [done]);

  // Rotate facts every 4 s with a smooth fade
  useEffect(() => {
    if (done) return;
    const t = setInterval(() => {
      setFactVisible(false);
      setTimeout(() => {
        setFactIdx(i => (i + 1) % FUN_FACTS.length);
        setFactVisible(true);
      }, 2600);
    }, 15000);
    return () => clearInterval(t);
  }, [done]);

  const lastIdx = steps.length - 1;

  return (
    <div className="thinking-shell thinking-shell-playful">
      <div className="thinking-center-block">

        {/* Brand-gradient wave — replaces old ColorWave */}
        <ThinkingWave active={!done} />

        {/* Headline */}
        <div className="thinking-top-copy">
          <p className="thinking-status-headline">
            {done ? 'Your episode package is ready.' : 'Building your unfair advantage…'}
          </p>
          {topic && <p className="thinking-topic-label">"{topic}"</p>}
        </div>

        {/* ── Steps ── */}
        <div className="tst-list">
          {steps.map((step, i) => {
            const isLast     = i === lastIdx;
            const isComplete = (i < currentIndex) || done;
            const isActive   = (i === currentIndex) && !done;

            // ── Final step → loading bar ──
            if (isLast) {
              return (
                <div key={step} className="tst-final-row">
                  {/* Connector from step above */}
                  <div className={`tst-connector${isComplete || isActive ? ' lit' : ''}`} />
                  <div className={`tst-bar-wrap${done ? ' done' : isActive ? ' active' : ''}`}>
                    <div className={`tst-bar-fill${done ? ' done' : ''}`} />
                    <span className="tst-bar-label">{step}</span>
                  </div>
                </div>
              );
            }

            // ── Regular steps → icon + label ──
            return (
              <div key={step} className={`tst-row${isComplete ? ' complete' : isActive ? ' active' : ' idle'}`}>
                {i > 0 && <div className={`tst-connector${isComplete ? ' lit' : ''}`} />}
                <div className="tst-row-inner">
                  <div className="tst-icon">
                    {isComplete ? <CheckIcon /> : isActive ? <div className="tst-pulse" /> : <div className="tst-ring" />}
                  </div>
                  <span className="tst-label">{step}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Rotating Did You Know ── */}
        <div className={`thinking-fun-fact${factVisible ? '' : ' fact-fade-out'}`}>
          <span className="thinking-fun-fact-label">Did you know</span>
          <p>{FUN_FACTS[factIdx]}</p>
        </div>

        {/* Elapsed timer */}
        {elapsed >= 5 && !done && (
          <p className="thinking-elapsed">
            {elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`} elapsed
          </p>
        )}

      </div>
    </div>
  );
}
